import { Link } from '@remix-run/react';
import {
    useStyles,
    Flex,
    Button,
    TileGrid,
    useTheme,
} from '@archipro-design/aria';
import * as S from './FeaturedProjects.style';
import ProjectTile from '~/modules/tile/component/project-tile/ProjectTile';
import type ProjectTileItem from '@modules/tile/type/project-tile-item';
import SaveToDesignBoard from '~/modules/design-board/component/SaveToDesignBoard';
import { useSaveToDesignBoard } from '~/modules/design-board/hook';
import { useTracker } from '@archipro-website/tracker';
import { useTileHotButtons } from '~/modules/root';

export interface FeaturedProjectsData {
    Professional: {
        ID: number;
        Title: string;
        Link: string;
    };
    FeaturedProjects: {
        pageInfo: {
            totalCount: number;
        };
        edges: {
            node: ProjectTileItem;
        }[];
    };
}

interface FeaturedProjectsProps {
    data: FeaturedProjectsData;
}

export const TILE_LIMIT = 4;
const TILE_LIMIT_DISPLAY = 3;

const FeaturedProjects = ({
    data: { Professional, FeaturedProjects },
}: FeaturedProjectsProps) => {
    const { css } = useStyles();
    const theme = useTheme();
    const { favouriteItem } = useSaveToDesignBoard('ProjectPin');
    const tracker = useTracker();

    const { getTileTopActions, getTileAdminCaption, hotButtonModal } =
        useTileHotButtons({
            itemType: 'project',
            itemIDs: FeaturedProjects.edges.map(
                ({ node: project }) => project.ID
            ),
        });

    const { edges, pageInfo } = FeaturedProjects;
    if (!edges?.length) return null;

    const totalCount = pageInfo.totalCount;

    const displayLoadMore = totalCount > TILE_LIMIT_DISPLAY;

    const viewMoreLink = `${Professional.Link}/case-studies`;

    return (
        <div className={css(S.FeaturedProjectsWrapper)}>
            <TileGrid
                heading={{
                    children: (
                        <>
                            More projects from
                            <br />
                            {Professional.Title}
                        </>
                    ),
                }}
                data-testid="projects-from-professional-grid"
                columns={TILE_LIMIT_DISPLAY}
            >
                {FeaturedProjects.edges
                    .slice(0, TILE_LIMIT_DISPLAY)
                    .map((project) => {
                        const topActions = getTileTopActions({
                            item: project.node,
                        });
                        const adminCaption = getTileAdminCaption(
                            project.node.ID
                        );

                        return (
                            <div
                                key={`featured-project-tile-${project.node.ID}`}
                            >
                                <ProjectTile
                                    data={project.node}
                                    size="large"
                                    favouriteItem={favouriteItem}
                                    onClick={() => {
                                        tracker.log('MoreProjectsFromClick', {
                                            url: new URL(window.location.href),
                                            targetTracker: 'archiproTracker',
                                        });
                                    }}
                                    topActions={topActions}
                                    adminCaption={adminCaption}
                                />
                            </div>
                        );
                    })}
            </TileGrid>
            {displayLoadMore && (
                <Flex hAlign="start">
                    <Button
                        as={Link}
                        prefetch="intent"
                        to={viewMoreLink}
                        size={16}
                        outlined={true}
                        className={css(S.LoadMoreButton)}
                        onClick={() => {
                            tracker.log('MoreProjectsFromExpandClick', {
                                url: new URL(window.location.href),
                                targetTracker: 'archiproTracker',
                            });
                        }}
                        variables={{
                            primaryButtonBackgroundColorNormal:
                                theme.siteVariables.colorScheme.default!
                                    .foreground,
                        }}
                    >
                        View more
                    </Button>
                </Flex>
            )}
            {favouriteItem.target && (
                <SaveToDesignBoard {...favouriteItem} autoReposition={false} />
            )}
            {hotButtonModal}
        </div>
    );
};

export default FeaturedProjects;
